import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshCw, Search, Users } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard, DataTable, EmptyState, LoadingState, ui } from "../../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { cn } from "../../../lib/utils";
import {
  CLAIM_CATEGORY,
  CLAIM_CATEGORY_LABEL_KEY,
  CLAIM_STATUS,
  CLAIM_STATUS_LABEL_KEY,
  abbreviationFor,
  formatAmount,
  formatClaimPeriod,
  formatDate,
} from "../constants/claimConstants";
import {
  ALL_CLAIMS_PATH,
  CANONICAL_ROLE,
  DETAIL_ORIGIN,
  claimDetailPath,
} from "../constants/approvalConstants";
import { fetchTeamClaims } from "../../../services/conveyanceClaimService";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import { selectBaseClass } from "../components/formControls";
import { useClaimCurrencies } from "../components/PolicyGate";
import {
  ApiPendingNotice,
  ApproverDeniedScreen,
  useApproverIdentity,
} from "../components/approverAccess";

const PAGE_SIZE = 10;
const ALL = "all";

/**
 * Screen 7 — Team Conveyance Claims (Team HRMS).
 *
 * A Reporting Manager's queue of claims raised by their direct reportees. This
 * is a triage and navigation screen only: the decision happens on Screen 6,
 * which is why every row leads there and why there is no bulk approve control
 * — a manager cannot responsibly approve a batch without opening each claim's
 * trip detail and proofs.
 *
 * Access is gated on the Manager canonical role.
 *
 * The screen holds filter and page state, sends it to the API, and renders the
 * response. Which claims belong in this queue, what order they come in, and
 * how many are still unactioned are all the server's answers, not this file's.
 */
export function TeamConveyanceClaimsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isReportingManager, isFinanceManager } = useApproverIdentity();
  const currencies = useClaimCurrencies();

  const [result, setResult] = useState({
    rows: [],
    totalCount: 0,
    pendingCount: 0,
    filterOptions: { submitters: [], months: [] },
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [status, setStatus] = useState(ALL);
  const [month, setMonth] = useState(ALL);
  const [submitterId, setSubmitterId] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const filtersActive =
    status !== ALL ||
    month !== ALL ||
    submitterId !== ALL ||
    category !== ALL ||
    search.trim() !== "";

  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");
    // A filter left at "all" is simply not sent.
    fetchTeamClaims({
      ...(status !== ALL && { status }),
      ...(month !== ALL && { month }),
      ...(submitterId !== ALL && { submitterId }),
      ...(category !== ALL && { category }),
      ...(search.trim() && { search: search.trim() }),
      page: page - 1,
      size: PAGE_SIZE,
    })
      .then(setResult)
      .catch((error) => {
        console.error("Failed to load team conveyance claims:", error);
        setResult({
          rows: [],
          totalCount: 0,
          pendingCount: 0,
          filterOptions: { submitters: [], months: [] },
        });
        setLoadError(error.message || t("conveyanceApproval.errorLoadClaims"));
      })
      .finally(() => setLoading(false));
  }, [status, month, submitterId, category, search, page, t]);

  useEffect(() => {
    if (isReportingManager) load();
    else setLoading(false);
  }, [isReportingManager, load]);

  /** Any filter change returns to page 1 — page 4 of the old result is meaningless. */
  const applyFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setStatus(ALL);
    setMonth(ALL);
    setSubmitterId(ALL);
    setCategory(ALL);
    setSearch("");
    setPage(1);
  };

  const openClaim = (claim) =>
    navigate(claimDetailPath(claim.claimId, { from: DETAIL_ORIGIN.TEAM }));

  // Mirror of the redirect on the finance register: a Finance member who
  // lands on the manager queue is sent to the tenant-wide list instead.
  if (!isReportingManager) {
    if (isFinanceManager) return <Navigate to={ALL_CLAIMS_PATH} replace />;
    return <ApproverDeniedScreen requiredRole={CANONICAL_ROLE.REPORTING_MANAGER} />;
  }

  const { rows, totalCount, pendingCount, filterOptions } = result;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const columns = [
    { key: "claimNo", label: t("conveyanceClaim.claimNo") },
    { key: "submitter", label: t("conveyanceApproval.submitter") },
    { key: "department", label: t("common.department") },
    { key: "category", label: t("conveyanceClaim.claimCategory") },
    { key: "period", label: t("conveyanceClaim.periodDate") },
    { key: "amount", label: t("conveyanceClaim.totalAmount"), className: "text-right" },
    { key: "status", label: t("conveyanceClaim.status") },
    { key: "submittedOn", label: t("conveyanceApproval.submittedOn") },
    { key: "action", label: t("common.action"), className: "text-right" },
  ];

  /**
   * Review while the claim is still waiting on this manager, View once it has
   * moved on. `awaitingAction` is the server's flag — the queue is ordered by
   * it too, so the label and the ordering can never disagree.
   */
  const rowActionLabel = (claim) =>
    claim.awaitingAction ? t("conveyanceApproval.review") : t("formActions.view");

  return (
    <AppLayout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className={ui.pageTitle}>{t("conveyanceApproval.teamClaimsTitle")}</h1>
            {pendingCount > 0 ? (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                style={{
                  backgroundColor: "var(--status-submitted-bg)",
                  color: "var(--status-submitted-fg)",
                }}
              >
                {t("conveyanceApproval.pendingCount", { count: pendingCount })}
              </span>
            ) : null}
          </div>
        </div>
        <div className="shrink-0 self-end sm:self-auto">
          <button type="button" onClick={load} className={ui.filterBtn}>
            <RefreshCw className="h-4 w-4" />
            {t("formActions.refresh")}
          </button>
        </div>
      </div>

      <ApiPendingNotice />

      {loadError ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 h-8 shrink-0 rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 hover:bg-rose-100 transition cursor-pointer"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("localConveyance.retry")}
          </button>
        </div>
      ) : null}

      {/* Filters — Status, Month, Submitter, Category, combinable, plus search
          on claim number or submitter name. */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="relative w-full sm:w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-secondary)]" />
          <input
            type="search"
            value={search}
            onChange={(event) => applyFilter(setSearch)(event.target.value)}
            placeholder={t("conveyanceApproval.searchPlaceholder")}
            className={cn(ui.input, "h-9 w-full pl-9")}
          />
        </div>

        <div className="w-[150px]">
          <Select value={status} onValueChange={applyFilter(setStatus)}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("conveyanceClaim.allStatuses")}</SelectItem>
              {Object.values(CLAIM_STATUS).map((value) => (
                <SelectItem key={value} value={value}>
                  {t(CLAIM_STATUS_LABEL_KEY[value])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[140px]">
          <Select value={month} onValueChange={applyFilter(setMonth)}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              <SelectItem value={ALL}>{t("conveyanceClaim.allMonths")}</SelectItem>
              {(filterOptions?.months || []).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[180px]">
          <Select value={submitterId} onValueChange={applyFilter(setSubmitterId)}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              <SelectItem value={ALL}>{t("conveyanceApproval.allSubmitters")}</SelectItem>
              {(filterOptions?.submitters || []).map((option) => (
                <SelectItem key={option.value} value={String(option.value)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[190px]">
          <Select value={category} onValueChange={applyFilter(setCategory)}>
            <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("conveyanceClaim.allCategories")}</SelectItem>
              {Object.values(CLAIM_CATEGORY).map((value) => (
                <SelectItem key={value} value={value}>
                  {t(CLAIM_CATEGORY_LABEL_KEY[value])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {filtersActive ? (
          <button type="button" onClick={resetFilters} className={ui.filterBtn}>
            {t("formActions.clearFilters")}
          </button>
        ) : null}
      </div>

      <ContentCard>
        {loading ? (
          <LoadingState message={t("conveyanceApproval.loadingClaims")} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Users}
            title={
              filtersActive
                ? t("conveyanceApproval.emptyFilteredTitle")
                : t("conveyanceApproval.teamEmptyTitle")
            }
            message={
              filtersActive
                ? t("conveyanceApproval.emptyFilteredMessage")
                : t("conveyanceApproval.teamEmptyMessage")
            }
          />
        ) : (
          <>
            <DataTable columns={columns} minWidth="1080px">
              {rows.map((claim) => (
                <tr
                  key={claim.claimId}
                  onClick={() => openClaim(claim)}
                  className={cn(
                    "cursor-pointer transition hover:bg-[var(--color-workspace)]/20",
                    // Unactioned claims sort to the top; tinting them keeps the
                    // boundary between the two groups visible after a scroll.
                    claim.awaitingAction && "bg-[var(--color-accent)]/[0.04]"
                  )}
                >
                  <td className="px-4 py-3 font-semibold text-foreground">
                    {claim.claimNo || `#${claim.claimId}`}
                  </td>
                  <td className="px-4 py-3 text-foreground">{claim.empName || "—"}</td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {claim.departmentName || "—"}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {formatClaimPeriod(claim, t)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-foreground">
                    {formatAmount(claim.totalAmount, abbreviationFor(currencies, claim.metricId))}
                  </td>
                  <td className="px-4 py-3">
                    <ClaimStatusBadge status={claim.claimStatus} />
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[var(--color-text-secondary)]">
                    {formatDate(claim.submittedOn, t) || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-[11px] font-bold text-[var(--color-primary)]">
                      {rowActionLabel(claim)}
                    </span>
                  </td>
                </tr>
              ))}
            </DataTable>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-[var(--color-text-border)]/40">
              {rows.map((claim) => (
                <button
                  key={claim.claimId}
                  type="button"
                  onClick={() => openClaim(claim)}
                  className={cn(
                    "w-full p-4 text-left transition hover:bg-[var(--color-workspace)]/20",
                    claim.awaitingAction && "bg-[var(--color-accent)]/[0.04]"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-foreground">
                        {claim.claimNo || `#${claim.claimId}`}
                      </p>
                      <p className="mt-0.5 text-[12px] text-[var(--color-text-secondary)] truncate">
                        {claim.empName || "—"}
                        {claim.departmentName ? ` · ${claim.departmentName}` : ""}
                      </p>
                    </div>
                    <ClaimStatusBadge status={claim.claimStatus} />
                  </div>

                  <p className="mt-2 text-lg font-extrabold text-foreground">
                    {formatAmount(claim.totalAmount, abbreviationFor(currencies, claim.metricId))}
                  </p>
                  <p className="text-[11px] text-[var(--color-text-secondary)]">
                    {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)} ·{" "}
                    {formatClaimPeriod(claim, t)}
                  </p>
                  <p className="mt-2 text-[11px] font-bold text-[var(--color-primary)]">
                    {rowActionLabel(claim)}
                  </p>
                </button>
              ))}
            </div>

            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </ContentCard>

      {/* Stated on the screen, not just in the spec: this list is for triage,
          and approval happens one claim at a time with its detail open. */}
      <p className="mt-3 text-[11px] text-[var(--color-text-secondary)]">
        {t("conveyanceApproval.noBulkApprovalNote")}
      </p>
    </AppLayout>
  );
}

export default TeamConveyanceClaimsPage;
